import { useEffect, useRef, useState } from "react";

const features = [
  {
    icon: "ri-shield-keyhole-line",
    title: "Military-Grade Security",
    desc: "AES-256 encryption protects every byte of data in transit. Your privacy is non-negotiable.",
    accent: "teal",
    size: "large",
  },
  {
    icon: "ri-speed-up-line",
    title: "Blazing Fast Performance",
    desc: "Optimized protocols ensure minimal speed loss so you browse, stream, and clean without interruption.",
    accent: "amber",
    size: "normal",
  },
  {
    icon: "ri-eye-off-line",
    title: "Zero Logging Policy",
    desc: "We never store, sell, or share your activity. What you do on your device stays private.",
    accent: "teal",
    size: "normal",
  },
  {
    icon: "ri-refresh-line",
    title: "Auto-Clean Scheduler",
    desc: "Set it and forget it. Schedule automatic cleanups to keep your iPhone running at peak speed.",
    accent: "amber",
    size: "normal",
  },
  {
    icon: "ri-image-2-line",
    title: "Smart Photo Analysis",
    desc: "AI-powered duplicate detection scans your photo library and suggests safe deletions, saving precious storage.",
    accent: "teal",
    size: "normal",
  },
  {
    icon: "ri-global-line",
    title: "Global Server Network",
    desc: "Access content from anywhere with servers in 50+ countries. Fast, reliable, and always available.",
    accent: "amber",
    size: "large",
  },
  {
    icon: "ri-notification-off-line",
    title: "Kill Switch Protection",
    desc: "Automatically blocks internet access if the VPN drops, so your real IP is never accidentally exposed.",
    accent: "teal",
    size: "normal",
  },
  {
    icon: "ri-battery-charge-line",
    title: "Battery-Friendly Design",
    desc: "Both apps are engineered to run efficiently in the background with minimal impact on battery life.",
    accent: "amber",
    size: "normal",
  },
];

function FeatureCard({ feature }: { feature: (typeof features)[0] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bg-[#0F1520] border border-white/8 rounded-2xl p-7 transition-all duration-700 hover:-translate-y-1 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div
        className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-5 ${
          feature.accent === "teal"
            ? "bg-teal-500/15 text-teal-400"
            : "bg-amber-500/15 text-amber-400"
        }`}
      >
        <i className={`${feature.icon} text-2xl`} />
      </div>
      <h4 className="text-white font-bold text-lg mb-3">{feature.title}</h4>
      <p className="text-white/50 text-sm leading-relaxed">{feature.desc}</p>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <section id="features" className="py-28 bg-[#080C14]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-teal-400 text-xs font-bold tracking-[0.3em] uppercase block mb-4">Features</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
            Everything You Need,{" "}
            <span className="text-teal-400">Nothing You Don&apos;t</span>
          </h2>
          <p className="text-white/50 text-lg max-w-xl mx-auto leading-relaxed">
            Carefully engineered features that work together to deliver a seamless, secure, and high-performance iOS experience.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature) => (
            <FeatureCard key={feature.title} feature={feature} />
          ))}
        </div>

        {/* Bottom Banner removed */}
      </div>
    </section>
  );
}
